import { useLocation } from 'react-use';
import { WarningText } from '@hungryroot/pho';
import { Paragraph } from '@hungryroot/ramen';
import { type ProductDetail } from '@hungryroot/rest';
import { tags as ProductTags } from '@hungryroot/tags';
import { useItemWarning } from '../../hooks/useItemWarning';
import { usePdpSubscriptionBanner } from '../../hooks/usePdpSubscriptionBanner';
import { AdditionalProductInfo } from '../AdditionalProductInfo/AdditionalProductInfo';
import { Divider } from '../Divider/Divider';
import { Header } from '../Header/Header';
import { Ingredients } from '../Ingredients/Ingredients';
import { Nutrition } from '../Nutrition/Nutrition';
import {
  formatNutritionValue,
  nutritionDataToNutritionTable,
  stringNumberToNumberNoDecimal,
} from '../Nutrition/utils';
import { OSNSection } from '../OSNSection/OSNSection';
import { RDPModal } from '../RDPModal/RDPModal';
import { SupplementNutrition } from '../SupplementNutrition/SupplementNutrition';
import { XButton } from '../XButton/XButton';
import { type SimplePDPModalContentsProps } from './types';

import styles from './SimplePDPModal.module.css';

// Extend ProductDetail to include discount field that may be present in API responses
interface ProductDetailWithDiscount extends ProductDetail {
  points_after_discount?: number;
}

function useShowNutritionHtml({ productV2 }: { productV2?: ProductDetail }) {
  const { pathname } = useLocation();

  if (!productV2) return false;

  const { primary_category_name: primaryCategoryName } = productV2;

  const pathIsLimitedTime = pathname === '/limited-time';

  const isMeatOrProduce =
    primaryCategoryName === 'Produce' ||
    primaryCategoryName === 'Meat + Seafood';

  const isLimitedTimeProduceOrMeat = pathIsLimitedTime && isMeatOrProduce;
  const isSupplement = primaryCategoryName === 'Supplements';

  return isSupplement || isLimitedTimeProduceOrMeat;
}

export function SimplePDPModalContents(props: SimplePDPModalContentsProps) {
  const {
    testid,
    onDismiss,
    footerButton,
    header,
    overlayClassName,
    primaryTopIconButton,
    secondaryTopIconButton,
    productV2,
    useServingUnits,
    warningComponentOverride,
    onImageScroll,
    showOSNSection = false,
    excludeCredits,
    onProductContentScroll,
    onShowMoreNutrition,
    onProductXClick,
  } = props;

  const isLoading = productV2 === undefined;
  const name = productV2?.name || '';
  const imageUrl = productV2?.card_image_url;
  const numRatings = productV2?.num_ratings;

  // Dev note: pre-checkout, we want `serving_units` to be the servings. post-checkout this is no longer true
  const servings =
    useServingUnits && productV2?.serving_units
      ? productV2.serving_units
      : productV2?.servings;

  const nutrition = productV2?.nutrition;
  const packagedImageUrl = productV2?.packaged_image2_url;
  const badges = productV2?.badges;
  const rating = productV2?.product_score;
  const brandName = productV2?.brand_name;
  const remark = productV2?.remark;
  const instruction = productV2?.instruction;
  const tags = productV2?.tags;
  const description = productV2?.description;
  const ingredientsHtml = productV2?.ingredients_html;
  const nutritionHtml = productV2?.nutrition_html;

  const showNutritionHtml = useShowNutritionHtml({
    productV2,
  });

  const subscriptionBanner = usePdpSubscriptionBanner(productV2?.id);

  const warning = useItemWarning(productV2);

  const getWarningComponent = () => {
    if (warningComponentOverride) {
      return warningComponentOverride;
    }

    if (warning) {
      return (
        <WarningText
          intensity={warning.warningTextIntensity}
          testid="warning-text"
        >
          {warning.warningText}
        </WarningText>
      );
    }

    return subscriptionBanner;
  };

  // Calculate discount values
  const credits = productV2?.points ? Number(productV2.points) : undefined;
  const pointsAfterDiscountValue = (productV2 as ProductDetailWithDiscount)
    ?.points_after_discount;
  const pointsAfterDiscount =
    pointsAfterDiscountValue !== undefined
      ? Number(pointsAfterDiscountValue)
      : undefined;

  const discount =
    credits !== undefined && pointsAfterDiscount !== undefined
      ? credits - pointsAfterDiscount
      : undefined;

  const discountPercentage =
    discount !== undefined && credits && credits > 0
      ? Math.round((discount / credits) * 100)
      : undefined;

  return (
    <RDPModal
      testid={testid}
      credits={excludeCredits === true ? undefined : credits}
      pointsAfterDiscount={pointsAfterDiscount}
      discount={discount}
      discountPercentage={discountPercentage}
      overlayClassName={overlayClassName}
      className={styles['pdp-modal']}
      brandName={brandName}
      isLoading={isLoading}
      ariaLabel={name}
      isOpen={true}
      imageUrl={imageUrl === null ? undefined : imageUrl}
      packagedImageUrl={packagedImageUrl}
      title={name}
      primaryTopRightIconButton={
        <XButton
          onClick={() => {
            onProductXClick?.();
            onDismiss();
          }}
        />
      }
      primaryTopIconButton={primaryTopIconButton}
      secondaryTopIconButton={secondaryTopIconButton}
      warningComponent={getWarningComponent()}
      onImageScroll={onImageScroll}
      onProductContentScroll={onProductContentScroll}
      productInfo={[
        {
          label: 'Rating',
          value: rating
            ? `${stringNumberToNumberNoDecimal(
                rating.toString()
              )}% (${numRatings})`
            : 'New',
        },
        {
          label: 'Serving',
          value: `${servings}`,
        },
        {
          label: 'Cal per serv.',
          value: `${nutrition ? nutrition.calories : ''}`,
        },
      ]}
      badges={badges}
      badgesOverride={props.badgesOverride}
      onDismiss={onDismiss}
      header={
        header ?? (
          <Header
            primaryTopRightIconButton={
              <XButton
                onClick={() => {
                  onProductXClick?.();
                  onDismiss();
                }}
                size="medium"
                prominence="tertiary"
                style="muted"
              />
            }
          />
        )
      }
    >
      <AdditionalProductInfo
        description={description}
        refrigerate={Boolean(
          !tags?.find((tag) => tag.id === ProductTags.NoRefrigeration)
        )}
        instructions={instruction}
        tip={remark}
      />

      {productV2 && showOSNSection ? (
        <OSNSection productId={productV2.id} />
      ) : null}

      {showNutritionHtml && nutritionHtml ? (
        <>
          <SupplementNutrition nutritionHtml={nutritionHtml} />
          <Divider />
        </>
      ) : (
        <>
          <Nutrition
            onShowMoreNutrition={onShowMoreNutrition}
            servings={
              <>
                <Paragraph>Servings per container: {servings}</Paragraph>
                {nutrition?.serving_size_text && (
                  <Paragraph className={styles['amount-per-serving']}>
                    Serving size: {nutrition.serving_size_text}
                  </Paragraph>
                )}
              </>
            }
            productInfo={[
              {
                label: 'Calories',
                value: `${
                  nutrition
                    ? stringNumberToNumberNoDecimal(
                        nutrition.calories.toString()
                      )
                    : ''
                }`,
              },
              {
                label: 'Fat',
                value: `${
                  nutrition
                    ? formatNutritionValue(nutrition.total_fat, 'total_fat')
                    : ''
                }`,
              },
              {
                label: 'Carbs',
                value: `${
                  nutrition
                    ? formatNutritionValue(nutrition.total_carb, 'total_carb')
                    : ''
                }`,
              },
              {
                label: 'Protein',
                value: `${
                  nutrition
                    ? formatNutritionValue(nutrition.protein, 'protein')
                    : ''
                }`,
              },
              {
                label: 'Sugars',
                value: `${
                  nutrition
                    ? formatNutritionValue(nutrition.sugars, 'sugars')
                    : ''
                }`,
              },
            ]}
            sections={nutrition ? nutritionDataToNutritionTable(nutrition) : []}
            disclaimer="The % Daily Value (DV) is based on a 2,000 calorie diet."
          />
          <Divider />
        </>
      )}

      {ingredientsHtml && (
        <>
          <Ingredients ingredients={ingredientsHtml} brandName={brandName} />
          <Divider />
        </>
      )}
      {footerButton}
    </RDPModal>
  );
}
